import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Paper from '@material-ui/core/Paper';

import { ActionBlockedForResponsivenessMessage } from './action-blocked-for-responsiveness-message';
import { AutomationsActionsMenu } from './automation-actions-menu';
import { createOrUpdateJobOverride } from '../../../applicant-automations/actions/create-or-update-job-override';
import { removeApplicantAutomationOverrides } from '../../../applicant-automations/actions/remove-applicant-automation-overrides';
import { updateApplicantAutomation } from '../../../applicant-automations/actions/update-applicant-automation';
import { applicantAutomationsForOrgIdSelector } from '../../../applicant-automations/applicant-automation-selectors';
import {
  EMPLOYEE_STATUS,
  loadOrgEmployeesByStatus,
} from '../../../employees/actions/load-org-employees-by-status';
import { loadOrgJobs } from '../../../job/actions/load-org-jobs';
import { userSelector } from '../../../reducers/current-user-selectors';
import { employeesForOrgIdSelector } from '../../../reducers/employee-selectors';
import { jobsForOrgIdSelector } from '../../../reducers/job-selectors';
import { updateStartDateTriggeredCommunication } from '../../../start-date-triggered-communication/actions/update-start-date-triggered-communication';

import { issueFilters } from '../../../employees/employee-list';
import { UiGreenSwitch } from '../../../ui/ui-green-switch';
import { UiHideShowToggle } from '../../../ui/ui-hide-show-toggle';

import { ApplicantAutomationComponent } from './applicant-automation-component';
import { AutomationComponentDetails } from './automation-component-details';
import { CopyAutomationDialog } from './copy-automation-dialog';
import { DeleteAutomationDialog } from './delete-automation-dialog';
import { EditAutomationDialog } from './edit-automation-dialog';
import { EmployeeAutomationComponent } from './employee-automation-component';
import { JobOverridesDialog } from './job-overrides-dialog';

import { languageHasActiveSpeakingEmployees } from '../../../employees/utils/language-has-active-speaking-employees';
import { JobOverrideIcon } from '../../../util/material-design-icons';
import { EMPLOYEE_PERMISSION } from '../../../util/permissions';
import { START_DATE_TRIGGERED_COMMUNICATION_TYPES } from '../../../util/start-date-triggered-communication-types';
import { useThunkDispatch } from '../../../util/use-thunk-dispatch';
import { confirm } from '../../../widgets/confirmation-dialog';
import {
  automationNeededForHqcMetric,
  AutomationsByTriggerDictionary,
  ConfirmRevertToDefaultText,
  getAllOverridesForOrgDefault,
  getTitleWithFallback,
} from '../../utils/applicant-automation-display-utils';
import {
  canEditEmployeeAutomations,
  canEditApplicantAutomationOrgDefaults,
  canViewApplicantAutomations,
} from '../../utils/automation-permission-utils';
import { calculateEmployeePercentage } from '../../utils/automation-warning-utils';
import { getAlertEmployeeRecipients } from '../../utils/employee-automation-display-utils';
import { isApplicantAutomation } from '../../utils/is-applicant-automation';

export const AutomationCard = ({
  automation,
  org,
  editingForJob,
  section,
  followUpsForSection,
  isViewing,
  toggleIsViewing,
  parentAutomationsForSection,
}: {
  automation: ApplicantAutomation | StartDateTriggeredCommunication;
  org: Org;
  editingForJob?: Job;
  section?: ApplicantAutomationSection;
  followUpsForSection?: ApplicantAutomation[];
  isViewing: boolean;
  toggleIsViewing: () => void;
  parentAutomationsForSection?: AutomationsByTriggerDictionary;
}) => {
  const user = useSelector((state: any) => userSelector(state));
  const hasApplicantAutomationEditPermission =
    !!editingForJob || canEditApplicantAutomationOrgDefaults(user);
  const hasApplicantAutomationViewPermission = canViewApplicantAutomations(user);
  const hasEmployeeAutomationEditPermission = canEditEmployeeAutomations(user);

  const [isEditingRecipientsOnly, setIsEditingRecipientsOnly] = useState<boolean>(false);
  const [editingAutomation, setEditingAutomation] = useState<
    ApplicantAutomation | StartDateTriggeredCommunication | null
  >(null);
  const [copyingAutomation, setCopyingAutomation] = useState<
    ApplicantAutomation | StartDateTriggeredCommunication | null
  >(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [deletingAutomation, setDeletingAutomation] = useState<
    ApplicantAutomation | StartDateTriggeredCommunication | null
  >(null);
  const [blockedActionType, setBlockedActionType] = useState<
    'revert' | 'delete' | 'turn off' | null
  >(null);
  const [showJobOverridesDialog, setShowJobOverridesDialog] = useState<boolean>(false);
  const [isApplicationQuestionsToggleDisabled, setIsApplicationQuestionsToggleDisabled] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();

  useEffect(() => {
    if (user.employeesPermission === EMPLOYEE_PERMISSION.ALL) {
      dispatch(loadOrgEmployeesByStatus(org.org_id, EMPLOYEE_STATUS.CURRENT));
    }

    if (!editingForJob && hasApplicantAutomationViewPermission) {
      dispatch(loadOrgJobs(org.org_id));
    }
  });

  const jobs = useSelector((state: any) => jobsForOrgIdSelector(state, org.org_id)) || [];

  const employees = useSelector((state: any) => employeesForOrgIdSelector(state, org.org_id));
  const employeeRecipients: Employee[] =
    automation.communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT
      ? getAlertEmployeeRecipients({
          employees,
          alertRecipients: automation.StartDateTriggeredAlert?.StartDateTriggeredAlertRecipients,
          sendToSupervisors: automation.StartDateTriggeredAlert?.sendToSupervisors,
          selectedGroupIds: automation.EmployeeAutomationRecipients.map((r) => r.employeeGroupId),
        })
      : employees;
  const hasSpanishSpeakingRecipients = languageHasActiveSpeakingEmployees('es', employeeRecipients);

  const sectionHasFollowUps = Boolean(followUpsForSection && followUpsForSection.length);

  const title = isApplicantAutomation(automation)
    ? getTitleWithFallback(automation)
    : automation.description;

  const applicantAutomations = useSelector((state: any) =>
    applicantAutomationsForOrgIdSelector(state, org.org_id),
  );

  const jobOverrides =
    editingForJob || !isApplicantAutomation(automation)
      ? []
      : getAllOverridesForOrgDefault(automation, applicantAutomations);
  const hasJobOverrides = jobOverrides && jobOverrides.length;

  const toggleDeleteDialog =
    (automationToDelete: ApplicantAutomation | StartDateTriggeredCommunication | null) => () => {
      const action = 'delete';
      const cannotDeleteForHqcMetric =
        automationToDelete &&
        isApplicantAutomation(automationToDelete) &&
        automationNeededForHqcMetric(
          automationToDelete,
          applicantAutomations,
          org.org_id,
          editingForJob ? [editingForJob] : jobs,
          action,
        );

      if (cannotDeleteForHqcMetric) {
        toggleActionBlocked(action);
        return;
      }

      setIsDeleting(!isDeleting);
      setDeletingAutomation(automationToDelete);
    };

  const toggleEditDialog =
    (
      automationToEdit: ApplicantAutomation | StartDateTriggeredCommunication | null,
      recipientsOnly?: boolean,
    ) =>
    () => {
      setEditingAutomation(automationToEdit);
      setIsEditingRecipientsOnly(!!recipientsOnly);
    };

  const closeCopyDialog = () => {
    setCopyingAutomation(null);
  };

  const openCopyDialog =
    (automationToCopy: ApplicantAutomation | StartDateTriggeredCommunication) => () => {
      setCopyingAutomation(automationToCopy);
    };

  const currentEmployees = employees.filter((e) => e.isCurrentEmployee);
  const employeesMissingSupervisors = issueFilters.missingSupervisor.filter(currentEmployees);
  const link = issueFilters.missingSupervisor.link(org.org_id);
  const { percentNotInCategory: employeesSupervisorsPercent } = calculateEmployeePercentage(
    employeesMissingSupervisors.length,
    currentEmployees.length,
  );

  const isAutomationCardActive = Boolean(
    isApplicantAutomation(automation) ? automation.isActive : automation.isOn,
  );

  const handleRevertToDefault =
    (automationToRevert: ApplicantAutomation | StartDateTriggeredCommunication) => async () => {
      if (!isApplicantAutomation(automationToRevert)) {
        return;
      }

      const action = 'revert';
      const cannotRevertForHqcMetric = automationNeededForHqcMetric(
        automationToRevert,
        applicantAutomations,
        org.org_id,
        editingForJob ? [editingForJob] : jobs,
        action,
      );
      if (cannotRevertForHqcMetric) {
        toggleActionBlocked(action);
        return;
      }

      if (await confirm(ConfirmRevertToDefaultText)) {
        dispatch(
          removeApplicantAutomationOverrides(
            automationToRevert.applicantTriggeredCommunicationId,
            'deleteOneOverride',
          ),
        );
      }
    };

  const toggleActionBlocked = (action: 'revert' | 'delete' | 'turn off' | null) => {
    setBlockedActionType(action);
  };

  const toggleAutomationOnOff =
    (automationToUpdate: ApplicantAutomation | StartDateTriggeredCommunication) => async () => {
      if (isApplicantAutomation(automationToUpdate)) {
        const action = 'turn off';
        const cannotTurnOffForHqcMetric = automationNeededForHqcMetric(
          automationToUpdate,
          applicantAutomations,
          org.org_id,
          editingForJob ? [editingForJob] : jobs,
          action,
        );
        if (cannotTurnOffForHqcMetric) {
          toggleActionBlocked(action);
          return;
        }

        const { applicantTriggeredCommunicationId, isActive } = automationToUpdate;
        if (editingForJob) {
          dispatch(
            createOrUpdateJobOverride(automationToUpdate, {
              jobId: editingForJob.job_id,
              isActive: !isActive,
            }),
          );
        } else {
          dispatch(
            updateApplicantAutomation(applicantTriggeredCommunicationId, {
              isActive: !isActive,
            }),
          );
        }

        if (isActive && !automationToUpdate.isFollowUp && sectionHasFollowUps) {
          const hasActiveFollowUps = !followUpsForSection?.every((followUp) => !followUp.isActive);
          const turnOffFollowUps = hasActiveFollowUps
            ? await confirm(
                'The automation you just turned off has follow up messages. Would you like to turn off any active follow ups as well?',
                { title: 'Turn Off Follow Up Automations', cancelButtonLabel: 'No Thanks' },
              )
            : false;

          if (turnOffFollowUps) {
            followUpsForSection?.forEach((followUp) => {
              dispatch(
                updateApplicantAutomation(followUp.applicantTriggeredCommunicationId, {
                  isActive: !isActive,
                }),
              );
            });
          }
        }
      } else {
        dispatch(
          updateStartDateTriggeredCommunication(
            automationToUpdate.startDateTriggeredCommunicationId,
            { isOn: !automationToUpdate.isOn },
            org.org_id,
          ),
        );
      }
    };

  const toggleUseApplicationQuestions =
    (automationToUpdate: ApplicantAutomation, isUsingApplicationQuestions: boolean) => async () => {
      setIsApplicationQuestionsToggleDisabled(true);
      // If we are editing at the job-level, we will use the createOrUpdate helper
      if (editingForJob) {
        await thunkDispatch(
          createOrUpdateJobOverride(automationToUpdate, {
            jobId: editingForJob.job_id,
            isUsingApplicationQuestions,
          }),
        );
      } else {
        await thunkDispatch(
          updateApplicantAutomation(automationToUpdate.applicantTriggeredCommunicationId, {
            isUsingApplicationQuestions,
          }),
        );
      }
      setIsApplicationQuestionsToggleDisabled(false);
    };

  const toggleShowJobOverridesDialog = () => {
    setShowJobOverridesDialog(!showJobOverridesDialog);
  };

  return (
    <div className="u-flex-center u-margin-x-2x u-width-100">
      <Paper
        className={`u-margin-y u-padding-lg u-width-100 ${
          isAutomationCardActive ? '' : 'u-background-lightGrayMuted'
        }`}
      >
        <div className="u-spread">
          <div>
            <div className="typo-title">{title}</div>
            <AutomationComponentDetails
              automation={automation}
              toggleEditRecipients={toggleEditDialog(automation, true)}
            />
          </div>
          <div>
            <UiGreenSwitch
              checked={isAutomationCardActive}
              onChange={toggleAutomationOnOff(automation)}
            />
            <AutomationsActionsMenu
              automation={automation}
              toggleDeleteDialog={toggleDeleteDialog(automation)}
              toggleEditDialog={toggleEditDialog(automation)}
              toggleCopyDialog={openCopyDialog(automation)}
              handleRevert={handleRevertToDefault(automation)}
            />
          </div>
        </div>
        <UiHideShowToggle title="Details" isOpen={isViewing} onChange={toggleIsViewing} />
        {isViewing &&
          (isApplicantAutomation(automation) ? (
            <ApplicantAutomationComponent
              key={`automation-card-${automation.applicantTriggeredCommunicationId}`}
              automationToRender={automation}
              toggleEdit={toggleEditDialog(automation)}
              handleRevert={handleRevertToDefault(automation)}
              turnUseApplicantQuestionsOn={toggleUseApplicationQuestions(automation, true)}
              turnUseApplicantQuestionsOff={toggleUseApplicationQuestions(automation, false)}
              applicationQuestionsToggleDisabled={isApplicationQuestionsToggleDisabled}
              orgName={org.name}
              canEditAutomation={hasApplicantAutomationEditPermission}
              jobOverrides={jobOverrides}
              toggleShowJobOverridesDialog={toggleShowJobOverridesDialog}
            />
          ) : (
            <EmployeeAutomationComponent
              automationToRender={automation}
              hasSpanishSpeakingRecipients={hasSpanishSpeakingRecipients}
              hasMissingSupervisorsIssue={!!employeesMissingSupervisors.length}
              canEditEmployeeAutomations={hasEmployeeAutomationEditPermission}
              orgId={org.org_id}
              orgName={org.name}
              missingSupervisorsIssueLink={link}
              toggleEdit={toggleEditDialog(automation)}
              hasSupervisorsPercent={employeesSupervisorsPercent}
            />
          ))}
      </Paper>
      <div className="u-width-30px u-margin-left-lg u-alignSelf-flexStart u-margin-top-xl typo-subtitle">
        {hasJobOverrides ? (
          <div
            role="presentation"
            className="u-cursor-pointer typo-link-secondary"
            onClick={toggleShowJobOverridesDialog}
          >
            <div
              className={`u-position-absolute u-margin-top-md u-padding-left-sm  ${
                jobOverrides.length > 9 ? 'u-margin-left-lg' : 'u-margin-left-xl'
              }`}
            >
              {jobOverrides.length > 9 ? '9+' : jobOverrides.length}
            </div>
            <JobOverrideIcon />
          </div>
        ) : null}
      </div>
      {!!editingAutomation && (
        <EditAutomationDialog
          editingAutomation={editingAutomation}
          org={org}
          onClose={toggleEditDialog(null)}
          isEditingRecipientsOnly={isEditingRecipientsOnly}
          title={title}
          editingForJob={editingForJob}
          applicantAutomationSection={section}
          parentAutomationsForSection={parentAutomationsForSection}
        />
      )}
      {!!copyingAutomation && (
        <CopyAutomationDialog
          copiedAutomation={copyingAutomation}
          org={org}
          onClose={closeCopyDialog}
          title={title}
          editingForJob={editingForJob}
          applicantAutomationSection={section}
          parentAutomationsForSection={parentAutomationsForSection}
        />
      )}
      {isDeleting && deletingAutomation && (
        <DeleteAutomationDialog
          orgId={org.org_id}
          automation={deletingAutomation}
          onClose={toggleDeleteDialog(null)}
          turnOffAutomation={toggleAutomationOnOff(deletingAutomation)}
          hasFollowUps={sectionHasFollowUps}
        />
      )}
      {blockedActionType && (
        <ActionBlockedForResponsivenessMessage
          variant="dialog"
          job={editingForJob}
          onClose={toggleActionBlocked}
          action={blockedActionType}
        />
      )}
      {showJobOverridesDialog ? (
        <JobOverridesDialog
          orgId={org.org_id}
          orgName={org.name}
          jobOverrides={jobOverrides}
          handleToggleDialog={toggleShowJobOverridesDialog}
        />
      ) : null}
    </div>
  );
};
